import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import * as url from "../../helpers/url_helper";

const TYPE_CONFIG = {
    text: ["text", "number", "email"],
    selection: ["radio", "checkbox", "dropdown"],
    organization: ["university"],
    information: ["red", "green", "black", "blue", "amber"],
};

export default function FormBuilder() {
    const { id: editFormId } = useParams();
    const navigate = useNavigate();
    const isEditMode = Boolean(editFormId);

    const emptyForm = {
        title: "",
        description: "",
        termsAndConditionsUrl: "",
        fields: [],
        isActive: true,
    };

    const [form, setForm] = useState(emptyForm);
    const [loadingForm, setLoadingForm] = useState(false);
    const [loadedMeta, setLoadedMeta] = useState({ title: "", description: "" });

    const buildAuthHeader = () => {
        const token = localStorage.getItem("token");
        const authUser = JSON.parse(localStorage.getItem("authUser") || "null");
        const rawToken = token || authUser?.accessToken;
        if (!rawToken) return {};
        const authToken = rawToken.startsWith("Bearer ")
            ? rawToken
            : `Bearer ${rawToken}`;
        return { Authorization: authToken };
    };

    useEffect(() => {
        if (!isEditMode) {
            // Clear any leftover state from a previous edit session so
            // the create page always starts blank.
            setForm({
                title: "",
                description: "",
                termsAndConditionsUrl: "",
                fields: [],
                isActive: true,
            });
            setLoadedMeta({ title: "", description: "" });
            setLoadingForm(false);
            return;
        }

        let cancelled = false;
        const loadForm = async () => {
            setLoadingForm(true);
            try {
                const response = await fetch(
                    `${url.VUF_APP_API}forms/${editFormId}`,
                    {
                        method: "GET",
                        headers: {
                            Accept: "application/json",
                            "Content-Type": "application/json",
                            ...buildAuthHeader(),
                        },
                    }
                );

                const rawResponse = await response.text();
                let responseData = {};
                try {
                    responseData = rawResponse ? JSON.parse(rawResponse) : {};
                } catch {
                    responseData = {};
                }

                if (!response.ok) {
                    throw new Error(
                        responseData?.message ||
                            responseData?.error ||
                            `Failed to load form (HTTP ${response.status})`
                    );
                }

                const formData =
                    responseData?.data ||
                    responseData?.form ||
                    responseData?.result ||
                    responseData;

                const definition =
                    formData?.form_definition || formData || {};

                const normalizedFields = (definition.fields || []).map(
                    (f, idx) => ({
                        id: f.id || `${Date.now()}-${idx}`,
                        type: f.type || "text",
                        inputType:
                            f.inputType ||
                            (TYPE_CONFIG[f.type]?.[0] ?? null),
                        label: f.label || "",
                        required: Boolean(f.required),
                        options: Array.isArray(f.options) ? f.options : [],
                    })
                );

                if (cancelled) return;

                const rawIsActive =
                    formData?.isActive ??
                    definition?.isActive;

                const loadedTitle =
                    formData?.title || definition?.title || "";
                const loadedDescription =
                    formData?.description || definition?.description || "";
                const loadedTerms =
                    formData?.termsAndConditionsUrl ||
                    definition?.termsAndConditionsUrl ||
                    "";

                setForm({
                    title: loadedTitle,
                    description: loadedDescription,
                    termsAndConditionsUrl: loadedTerms,
                    fields: normalizedFields,
                    isActive: rawIsActive === undefined ? true : Boolean(rawIsActive),
                });
                setLoadedMeta({
                    title: loadedTitle,
                    description: loadedDescription,
                });
            } catch (e) {
                if (!cancelled) {
                    alert(e?.message || "Error loading form");
                }
            } finally {
                if (!cancelled) setLoadingForm(false);
            }
        };

        loadForm();
        return () => {
            cancelled = true;
        };
    }, [editFormId, isEditMode]);
 
    // ================= FIELD =================
    const addField = () => {
        setForm((prev) => ({
            ...prev,
            fields: [
                ...prev.fields,
                {
                    id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
                    type: "text",
                    inputType: "text",
                    label: "",
                    required: false,
                    options: [],
                },
            ],
        }));
    };

    const updateField = (i, key, value) => {
        setForm((prev) => {
            const updatedFields = prev.fields.map((field, idx) => {
                if (idx !== i) return field;
                const next = { ...field, [key]: value };
                if (key === "type") {
                    next.inputType = TYPE_CONFIG[value]?.[0] || null;
                    next.options = [];
                }
                return next;
            });
            return { ...prev, fields: updatedFields };
        });
    };

    const deleteField = (i) => {
        setForm((prev) => ({
            ...prev,
            fields: prev.fields.filter((_, idx) => idx !== i),
        }));
    };

    // ================= OPTIONS =================
    const addOption = (i) => {
        setForm((prev) => ({
            ...prev,
            fields: prev.fields.map((field, idx) =>
                idx === i
                    ? {
                          ...field,
                          options: [
                              ...(field.options || []),
                              { label: "", value: "" },
                          ],
                      }
                    : field
            ),
        }));
    };

    const updateOption = (fi, oi, key, value) => {
        setForm((prev) => ({
            ...prev,
            fields: prev.fields.map((field, idx) => {
                if (idx !== fi) return field;
                const updatedOptions = (field.options || []).map((opt, oIdx) =>
                    oIdx === oi ? { ...opt, [key]: value } : opt
                );
                return { ...field, options: updatedOptions };
            }),
        }));
    };

    const removeOption = (fi, oi) => {
        setForm((prev) => ({
            ...prev,
            fields: prev.fields.map((field, idx) =>
                idx === fi
                    ? {
                          ...field,
                          options: (field.options || []).filter(
                              (_, oIdx) => oIdx !== oi
                          ),
                      }
                    : field
            ),
        }));
    };
 
    // ================= SUBMIT =================
    const handleSubmit = async () => {
        try {
            const submittedFormData = {
                title: form.title.trim(),
                description: form.description,
                termsAndConditionsUrl: (form.termsAndConditionsUrl || "").trim(),
                fields: form.fields,
            };

            const payload = {
                title: submittedFormData.title,
                description: submittedFormData.description,
                termsAndConditionsUrl: submittedFormData.termsAndConditionsUrl,
                // Keep exact form input structure in form_definition as requested.
                form_definition: submittedFormData,
                ...(isEditMode ? { isActive: Boolean(form.isActive) } : {}),
            };

            if (!form.title.trim()) {
                alert("Please enter form title");
                return;
            }

            if (!submittedFormData.termsAndConditionsUrl) {
                alert("Please enter terms and conditions URL");
                return;
            }

            if (!form.fields.length) {
                alert("Please add at least one field");
                return;
            }

            const authHeader = buildAuthHeader();

            const endpoint = isEditMode
                ? `${url.VUF_APP_API}forms/${editFormId}`
                : `${url.VUF_APP_API}forms`;
            const method = isEditMode ? "PUT" : "POST";

            const response = await fetch(endpoint, {
                method,
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                    ...authHeader,
                },
                body: JSON.stringify(payload),
            });

            const rawResponse = await response.text();
            let responseData = {};
            try {
                responseData = rawResponse ? JSON.parse(rawResponse) : {};
            } catch {
                responseData = {
                    message: rawResponse || "Unexpected server response",
                };
            }

            if (!response.ok) {
                throw new Error(
                    responseData?.message ||
                    responseData?.error ||
                    `Failed to ${isEditMode ? "update" : "create"} form (HTTP ${response.status})\n${
                        typeof responseData === "object"
                            ? JSON.stringify(responseData)
                            : String(responseData || "")
                    }`
                );
            }

            const savedForm =
                responseData?.data || responseData?.form || responseData;

            console.log("Submitted form payload:", payload);
            console.log("Submitted form endpoint:", endpoint);
            console.log("Saved form response:", savedForm);

            const savedFormId =
                responseData?.createdId ||
                responseData?.formId ||
                responseData?.data?.createdId ||
                responseData?.data?.formId ||
                savedForm?._id ||
                savedForm?.id ||
                editFormId ||
                "N/A";

            if (isEditMode) {
                // alert(
                //     `Form updated successfully\n\nForm: ${form.title}\nID: ${savedFormId}\nFields: ${form.fields.length}`
                // );
                alert(
                    `Form updated successfully`
                );
                navigate("/forms-list");
            } else {
                // alert(
                //     `Form created successfully\n\nForm: ${form.title}\nCreated ID: ${savedFormId}\nFields: ${form.fields.length}\n\nPayload Sent:\n${JSON.stringify(payload, null, 2)}`
                // );
                alert(
                    `Form created successfully`
                );
            }
        } catch (e) {
            alert(e?.message || `Error ${isEditMode ? "updating" : "creating"} form`);
        }
    };
 
    return (
        <div style={container}>
            {/* HEADER */}
            <div style={header}>
                <h2 style={{ margin: 0 }}>
                    {isEditMode ? "✏️ Edit Form" : "✨ Form Builder"}
                </h2>
                {isEditMode && (loadedMeta.title || loadedMeta.description) && (
                    <div style={headerMetaBox}>
                        {loadedMeta.title && (
                            <div style={headerMetaRow}>
                                <span style={headerMetaLabel}>Title:</span>
                                <span style={headerMetaValue}>
                                    {loadedMeta.title}
                                </span>
                            </div>
                        )}
                        {loadedMeta.description && (
                            <div style={headerMetaRow}>
                                <span style={headerMetaLabel}>Description:</span>
                                <span style={headerMetaValue}>
                                    {loadedMeta.description}
                                </span>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {loadingForm && (
                <div style={card}>
                    <p style={{ margin: 0 }}>Loading form...</p>
                </div>
            )}

            {/* FORM INFO */}
            <div style={card}>
                <input
                    placeholder="Form Title"
                    value={form.title}
                    onChange={(e) =>
                        setForm({ ...form, title: e.target.value })
                    }
                    style={input}
                />
 
                <textarea
                    placeholder="Description"
                    value={form.description}
                    onChange={(e) =>
                        setForm({ ...form, description: e.target.value })
                    }
                    style={input}
                />

                <input
                    type="url"
                    placeholder="Terms and Conditions URL *"
                    value={form.termsAndConditionsUrl}
                    onChange={(e) =>
                        setForm({
                            ...form,
                            termsAndConditionsUrl: e.target.value,
                        })
                    }
                    style={input}
                />

                {isEditMode && (
                    <div style={activeToggleRow}>
                        <span style={activeToggleLabel}>
                            {form.isActive ? "Active" : "Inactive"}
                        </span>
                        <button
                            type="button"
                            role="switch"
                            aria-checked={Boolean(form.isActive)}
                            onClick={() =>
                                setForm((prev) => ({
                                    ...prev,
                                    isActive: !prev.isActive,
                                }))
                            }
                            style={{
                                ...switchTrack,
                                background: form.isActive ? "#4a00e0" : "#bbb",
                            }}
                        >
                            <span
                                style={{
                                    ...switchKnob,
                                    left: form.isActive ? 26 : 2,
                                }}
                            />
                        </button>
                    </div>
                )}
            </div>
 
            {/* FIELDS */}
            {form.fields.map((field, i) => (
                <div key={field.id} style={card}>
                    {/* LABEL */}
                    <input
                        placeholder="Question Label"
                        value={field.label}
                        onChange={(e) =>
                            updateField(i, "label", e.target.value)
                        }
                        style={{ ...input, marginBottom: 15 }}
                    />
 
                    {/* TYPE + INPUT TYPE */}
                    <div style={row}>
                        <select
                            value={field.type}
                            onChange={(e) =>
                                updateField(i, "type", e.target.value)
                            }
                            style={select}
                        >
                            <option value="text">Text</option>
                            <option value="selection">Selection</option>
                            <option value="date">Date</option>
                            <option value="time">Time</option>
                            <option value="rating">Rating</option>
                            <option value="boolean">Switch</option>
                            <option value="file">File</option>
                            <option value="image">Image</option>
                            <option value="multi_text">Multi Text</option>
                            <option value="organization">Organization</option>
                            <option value="information">Information</option>
                        </select>
 
                        {TYPE_CONFIG[field.type] && (
                            <select
                                value={field.inputType}
                                onChange={(e) =>
                                    updateField(i, "inputType", e.target.value)
                                }
                                style={select}
                            >
                                {TYPE_CONFIG[field.type].map((t) => (
                                    <option key={t}>{t}</option>
                                ))}
                            </select>
                        )}
                    </div>
 
                    {/* OPTIONS */}
                    {field.type === "selection" && (
                        <div style={optionsBox}>
                            <div style={rowBetween}>
                                <strong>Options</strong>
                                <button
                                    type="button"
                                    style={smallBtn}
                                    onClick={() => addOption(i)}
                                >
                                    + Add
                                </button>
                            </div>
 
                            {field.options.map((opt, oi) => (
                                <div key={oi} style={row}>
                                    <input
                                        placeholder="Label"
                                        value={opt.label}
                                        onChange={(e) => {
                                            const label = e.target.value;
                                            updateOption(i, oi, "label", label);
                                            updateOption(
                                                i,
                                                oi,
                                                "value",
                                                label.toLowerCase().replace(/\s/g, "_")
                                            );
                                        }}
                                        style={input}
                                    />
 
                                    <input
                                        placeholder="Value"
                                        value={opt.value}
                                        onChange={(e) =>
                                            updateOption(i, oi, "value", e.target.value)
                                        }
                                        style={input}
                                    />
 
                                    <button
                                        type="button"
                                        style={deleteBtnSmall}
                                        onClick={() => removeOption(i, oi)}
                                    >
                                        ✕
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
 
                    {/* REQUIRED + DELETE */}
                    <div style={rowBetween}>
                        <div
                            role="checkbox"
                            tabIndex={0}
                            aria-checked={Boolean(field.required)}
                            onClick={() =>
                                updateField(i, "required", !field.required)
                            }
                            onKeyDown={(e) => {
                                if (e.key === " " || e.key === "Enter") {
                                    e.preventDefault();
                                    updateField(i, "required", !field.required);
                                }
                            }}
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                                cursor: "pointer",
                                userSelect: "none",
                            }}
                        >
                            <input
                                type="checkbox"
                                checked={Boolean(field.required)}
                                readOnly
                                tabIndex={-1}
                                style={{ pointerEvents: "none" }}
                            />
                            <span>Required</span>
                        </div>

                        <button
                            type="button"
                            style={deleteBtn}
                            onClick={() => deleteField(i)}
                        >
                            Delete
                        </button>
                    </div>
                </div>
            ))}
 
            {/* ACTION BUTTONS */}
            <div style={actionRow}>
                <button type="button" style={addBtn} onClick={addField}>
                    + Add Field
                </button>

                <button type="button" style={submitBtn} onClick={handleSubmit}>
                    {isEditMode ? "💾 Update Form" : "🚀 Submit Form"}
                </button>
            </div>
        </div>
    );
}
 
// ================= STYLES =================
 
const container = {
    maxWidth: 850,
    margin: "auto",
    padding: 20,
    background: "#f4f6ff",
    minHeight: "100vh",
};
 
const header = {
    background: "linear-gradient(90deg,#4a00e0,#8e2de2)",
    color: "#fff",
    padding: 20,
    borderRadius: 12,
    marginBottom: 20,
};
 
const card = {
    background: "#fff",
    padding: 20,
    borderRadius: 15,
    marginBottom: 20,
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
};
 
const input = {
    width: "95%",
    padding: 12,
    marginTop: 10,
    borderRadius: 10,
    border: "1px solid #ddd",
};
 
const row = {
    display: "flex",
    gap: 12,
    marginBottom: 12,
    alignItems: "center", // ⭐ this fixes vertical alignment
};
 
const rowBetween = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
};
 
const select = {
    flex: 1,
    padding: 12,
    borderRadius: 10,
    border: "1px solid #ddd",
    background: "#fafafa",
};
 
const optionsBox = {
    background: "#f8f9ff",
    padding: 12,
    borderRadius: 10,
    marginBottom: 15,
};
 
const actionRow = {
    display: "flex",
    gap: 15,
};
 
const addBtn = {
    flex: 1,
    padding: 15,
    background: "green",
    color: "#fff",
    border: "none",
    borderRadius: 12,
    cursor: "pointer",
};
 
const submitBtn = {
    flex: 1,
    padding: 15,
    background: "linear-gradient(90deg,#4a00e0,#8e2de2)",
    color: "#fff",
    border: "none",
    borderRadius: 12,
    cursor: "pointer",
};
 
const deleteBtn = {
    background: "#ff4d4d",
    color: "#fff",
    border: "none",
    padding: "10px 10px",
    borderRadius: 8,
    cursor: "pointer",
};
 
const deleteBtnSmall = {
    width: 42,
    height: 42,
    minWidth: 42,
    background: "#d32f2f", // dark red
    color: "#fff",
    border: "none",
    borderRadius: 10,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 16,
};
 
 
 
const smallBtn = {
    background: "#4a00e0",
    color: "#fff",
    border: "none",
    padding: "6px 10px",
    borderRadius: 8,
    cursor: "pointer",
};

const activeToggleRow = {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginTop: 15,
};

const activeToggleLabel = {
    fontWeight: 600,
    color: "#333",
    minWidth: 70,
};

const switchTrack = {
    position: "relative",
    display: "inline-block",
    boxSizing: "border-box",
    width: 50,
    minWidth: 50,
    height: 26,
    border: "none",
    padding: 0,
    margin: 0,
    borderRadius: 13,
    cursor: "pointer",
    transition: "background 0.2s",
    outline: "none",
    flexShrink: 0,
    verticalAlign: "middle",
};

const switchKnob = {
    position: "absolute",
    top: 2,
    width: 22,
    height: 22,
    background: "#fff",
    borderRadius: "50%",
    transition: "left 0.2s",
    boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
    pointerEvents: "none",
};

const headerMetaBox = {
    marginTop: 12,
    background: "rgba(255,255,255,0.12)",
    borderRadius: 10,
    padding: "10px 14px",
    display: "flex",
    flexDirection: "column",
    gap: 4,
};

const headerMetaRow = {
    display: "flex",
    gap: 8,
    alignItems: "baseline",
    color: "#fff",
    fontSize: 14,
};

const headerMetaLabel = {
    opacity: 0.85,
    fontWeight: 600,
    minWidth: 84,
};

const headerMetaValue = {
    fontWeight: 500,
    wordBreak: "break-word",
};